import React, { useState, useEffect, useRef, useCallback } from 'react';
import { chatAPI } from '../api';
import '../styles/chat-voice.css';

const VoiceChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeMode, setActiveMode] = useState('chat');
  const [showVoiceScreen, setShowVoiceScreen] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [waveText, setWaveText] = useState('Talk to your AI assistant now');
  const [audioState, setAudioState] = useState('idle'); // 'idle' | 'listening' | 'speaking'
  const audioContextRef = useRef(null);
  const isListeningRef = useRef(false);
  const wantListeningRef = useRef(false);
  const currentUtteranceRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const lastTranscriptRef = useRef('');
  const autoLangRef = useRef('');

  // Configuration state - fetched from backend
  const [config, setConfig] = useState({
    webhookUrl: '',
    elevenLabsVoiceId: '',
    elevenLabsApiKey: '',
    sttLang: 'hi-IN'
  });

  // Fetch settings from backend
  const fetchSettings = useCallback(async () => {
    try {
      const settings = await chatAPI.getSettings();
      setConfig({
        webhookUrl: settings.webhookUrl || '',
        elevenLabsVoiceId: settings.elevenLabsVoiceId || '',
        elevenLabsApiKey: settings.elevenLabsApiKey || '',
        sttLang: settings.sttLang || 'hi-IN'
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);


  // Append message to chat
  const appendMessage = useCallback((text, role = 'bot', timestamp = new Date()) => {
    const message = {
      id: Date.now(),
      text,
      role,
      timestamp
    };
    setMessages(prev => [...prev, message]);
  }, []);

  // Format time
  const formatTime = useCallback((timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Show toast notification
  const showToast = useCallback((message) => {
    // Implement toast functionality
    console.log('Toast:', message);
  }, []);

  // Speech recognition functions
  const supportedLangFor = useCallback((text) => {
    const hasDevanagari = /[\u0900-\u097F]/.test(text);
    const hasOdia = /[\u0B00-\u0B7F]/.test(text);
    if (hasOdia) return 'or-IN';
    if (hasDevanagari) return 'hi-IN';
    return 'en-IN';
  }, []);

  // Initialize audio context
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current && 'AudioContext' in window) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Safe start listening function with guards
  const startListening = useCallback(() => {
    if (speechSynthesis.speaking || audioState === 'speaking') {
      console.log('Cannot start listening - TTS is speaking');
      return false;
    }

    if (!recognitionRef.current) {
      console.error('Recognition not initialized');
      return false;
    }

    try {
      // Resume audio context before starting recognition
      const audioContext = initAudioContext();
      if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume();
      }

      recognitionRef.current.start();
      setAudioState('listening');
      isListeningRef.current = true;
      console.log('Started listening');
      return true;
    } catch (error) {
      console.error('Failed to start recognition:', error);
      return false;
    }
  }, [audioState, initAudioContext]);

  // Safe stop listening function
  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListeningRef.current) {
      return;
    }

    try {
      recognitionRef.current.abort();
      isListeningRef.current = false;
      console.log('Stopped listening');
    } catch (error) {
      console.error('Failed to stop recognition:', error);
    }
  }, []);

  // Start TTS and handle state transitions - use backend TTS API
  const startTTS = useCallback(async (text) => {
    // Immediately stop any ongoing listening
    stopListening();
    
    // Cancel any previous TTS
    speechSynthesis.cancel();
    if (currentUtteranceRef.current) {
      currentUtteranceRef.current = null;
    }

    // Set speaking state
    setAudioState('speaking');
    
    // Suspend audio context during TTS
    const audioContext = initAudioContext();
    if (audioContext && audioContext.state === 'running') {
      audioContext.suspend();
    }

    try {
      // Use backend TTS API which handles user-specific settings
      const audioData = await chatAPI.textToSpeech(text);
      
      // Convert base64 audio data to blob
      const audioBlob = new Blob([Uint8Array.from(atob(audioData), c => c.charCodeAt(0))], {
        type: 'audio/mpeg'
      });
      
      // Create audio element and play the audio
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      return new Promise((resolve) => {
        audio.onended = () => {
          console.log('TTS ended');
          URL.revokeObjectURL(audioUrl);
          setAudioState('idle');
          resolve();
        };
        
        audio.onerror = (error) => {
          console.error('Audio playback error:', error);
          // Fallback to browser TTS
          const utterance = new SpeechSynthesisUtterance(text);
          const lang = config.sttLang === 'auto' ? (autoLangRef.current || 'en-IN') : config.sttLang;
          utterance.lang = lang;
          currentUtteranceRef.current = utterance;
          
          utterance.onend = () => {
            console.log('Browser TTS ended');
            setAudioState('idle');
            resolve();
          };
          
          utterance.onerror = (error) => {
            console.error('Browser TTS error:', error);
            setAudioState('idle');
            resolve();
          };
          
          speechSynthesis.speak(utterance);
        };
        
        audio.play().catch((error) => {
          console.error('Audio play failed:', error);
          // Fallback to browser TTS
          const utterance = new SpeechSynthesisUtterance(text);
          const lang = config.sttLang === 'auto' ? (autoLangRef.current || 'en-IN') : config.sttLang;
          utterance.lang = lang;
          currentUtteranceRef.current = utterance;
          
          utterance.onend = () => {
            console.log('Browser TTS ended');
            setAudioState('idle');
            resolve();
          };
          
          utterance.onerror = (error) => {
            console.error('Browser TTS error:', error);
            setAudioState('idle');
            resolve();
          };
          
          speechSynthesis.speak(utterance);
        });
      });
    } catch (error) {
      console.error('TTS error:', error);
      // Fallback to browser TTS
      const utterance = new SpeechSynthesisUtterance(text);
      const lang = config.sttLang === 'auto' ? (autoLangRef.current || 'en-IN') : config.sttLang;
      utterance.lang = lang;
      currentUtteranceRef.current = utterance;
      
      return new Promise((resolve) => {
        utterance.onend = () => {
          console.log('Browser TTS ended');
          setAudioState('idle');
          resolve();
        };
        
        utterance.onerror = (error) => {
          console.error('Browser TTS error:', error);
          setAudioState('idle');
          resolve();
        };
        
        speechSynthesis.speak(utterance);
      });
    }
  }, [config.sttLang, stopListening, initAudioContext]);

  // Auto-restart listening when TTS ends and we're in call
  useEffect(() => {
    if (audioState === 'idle' && inCall && wantListeningRef.current) {
      console.log('Auto-restarting listening after TTS');
      startListening();
    }
  }, [audioState, inCall, startListening]);


  const initRecognition = useCallback(() => {
    if (recognitionRef.current || !('webkitSpeechRecognition' in window)) return;

    try {
      const SpeechRecognition = window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false; // Changed to false as per requirements
      recognitionRef.current.interimResults = false; // Changed to false as per requirements
      recognitionRef.current.lang = config.sttLang === 'auto' ? 'en-IN' : config.sttLang;

      recognitionRef.current.onerror = (e) => {
        console.warn('STT error', e);
        showToast('Speech recognition error');
      };

      recognitionRef.current.onend = () => {
        // Only restart if we're not speaking and want to listen
        if (inCall && audioState !== 'speaking' && wantListeningRef.current) {
          console.log('Restarting recognition after end');
          setTimeout(() => {
            try {
              if (audioState !== 'speaking') {
                recognitionRef.current.start();
              }
            } catch (error) {
              console.error('Failed to restart recognition:', error);
            }
          }, 300); // Debounce to avoid double triggers
        }
      };

      recognitionRef.current.onresult = async (event) => {
        let interim = '';
        let finalText = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalText += result[0].transcript;
          } else {
            interim += result[0].transcript;
          }
        }

        const displayText = (finalText.trim() || interim).trim();
        setWaveText(displayText || 'Listening…');

        if (config.sttLang === 'auto' && displayText) {
          const suggestedLang = supportedLangFor(displayText);
          if (suggestedLang && suggestedLang !== autoLangRef.current) {
            autoLangRef.current = suggestedLang;
            try {
              recognitionRef.current.abort();
              recognitionRef.current.lang = suggestedLang;
              recognitionRef.current.start();
              showToast(`STT: ${suggestedLang}`);
              return;
            } catch (error) {
              console.error('Language switch failed:', error);
            }
          }
        }

        finalText = finalText.trim();
        if (!finalText || finalText === lastTranscriptRef.current) return;
        lastTranscriptRef.current = finalText;

        appendMessage(finalText, 'user');
        
        if (config.webhookUrl) {
          try {
            setIsLoading(true);
            const response = await chatAPI.sendMessage(finalText);
            const reply = response.response || response.text || String(response);
            appendMessage(reply, 'bot');
            await startTTS(reply);
          } catch (error) {
            console.error('Webhook/STT error', error);
            appendMessage('Error processing voice message', 'bot');
          } finally {
            setIsLoading(false);
          }
        } else {
          setWaveText('Webhook URL missing — open Settings');
        }
      };
    } catch (error) {
      console.error('Speech recognition initialization failed:', error);
      showToast('Speech recognition not available');
    }
  }, [config.sttLang, config.webhookUrl, inCall, startTTS, appendMessage, showToast, supportedLangFor]);

  const startVoiceUI = () => {
    console.log('Starting voice UI - setting state to listening');
    initRecognition();
    setInCall(true);
    wantListeningRef.current = true;
    setAudioState('listening');
    setWaveText('Listening…');
    autoLangRef.current = config.sttLang === 'auto' ? '' : config.sttLang;
    startListening();
  };

  const stopAllVoice = () => {
    setInCall(false);
    wantListeningRef.current = false;
    setAudioState('idle');
    setWaveText('Talk to your AI assistant now');
    stopListening();
    speechSynthesis.cancel();
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    appendMessage(inputMessage, 'user');
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await chatAPI.sendMessage(inputMessage);
      const message = response.response || response.text || String(response);
      appendMessage(message, 'bot');
    } catch (error) {
      console.error('Error sending message:', error);
      appendMessage('Error sending message', 'bot');
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeChange = (mode) => {
    console.log('Mode changed to:', mode);
    setActiveMode(mode);
    
    if (mode === 'voice') {
      setShowVoiceScreen(true);
      startVoiceUI();
    } else {
      setShowVoiceScreen(false);
      stopAllVoice();
    }
  };

  const handleCallButtonClick = () => {
    if (!inCall) {
      startVoiceUI();
    } else {
      stopAllVoice();
    }
  };

  return (
    <div className="chat-voice-container">
      {/* Header */}
      <header className="chat-voice-header">
        <div className="chat-voice-brand">
          <div className="chat-voice-logo"><span>RT</span></div>
          <div>
            <div style={{ fontSize: '15px' }}>Rout Technical AI</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Smart assistant • Online</div>
          </div>
        </div>
        <div className="chat-voice-chip">Beta</div>
      </header>

      {/* Chat area */}
      <div className="chat-voice-wrap">
        <main className="chat-voice-messages" aria-live="polite">
          {messages.length === 0 ? (
            <div className="chat-voice-divider">Today</div>
          ) : null}
          
          {messages.map((message) => (
            <div
              key={message.id}
              className={`chat-voice-message ${
                message.role === 'user' ? 'chat-voice-message-user' : 'chat-voice-message-bot'
              }`}
            >
              {message.text}
              <small>{formatTime(message.timestamp)}</small>
            </div>
          ))}
          
          {isLoading && (
            <div className="chat-voice-message chat-voice-message-bot">
              Typing…
              <small>{formatTime(new Date())}</small>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </main>

        {/* Input Area */}
        <footer className="chat-voice-input-container">
          <div className="chat-voice-input-wrap">
            <input
              id="message-input"
              type="text"
              className="chat-voice-input"
              placeholder="Type your message…"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(e)}
              disabled={isLoading}
            />
            <button
              className="chat-voice-send-btn"
              type="button"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
            >
              <svg viewBox="0 0 24 24">
                <path d="M2 21l21-9L2 3v7l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </footer>
      </div>


      {/* Voice Screen */}
      {showVoiceScreen && (
        <section className="chat-voice-screen show" aria-live="polite">
          <div className="bg"></div>
          <div className="chat-voice-top">
            <button
              className="chat-voice-close"
              onClick={() => handleModeChange('chat')}
            >
              Close
            </button>
          </div>
          <div className="chat-voice-mic-shadow"></div>
          <div className="chat-voice-mic"></div>
          <button
            className={`chat-voice-mic-button ${inCall ? 'active' : ''}`}
            onClick={handleCallButtonClick}
          >
            <div className="chat-voice-mic-icon"></div>
          </button>
          <div className="chat-voice-wave-container show">
            <div className="chat-voice-wave-text">{waveText}</div>
            <div className="chat-voice-bars">
              <span></span><span></span><span></span><span></span><span></span>
            </div>
          </div>
        </section>
      )}

      {/* Bottom Dock */}
      <nav className="chat-voice-dock" aria-label="Mode controls">
        <button
          className={`chat-voice-dock-btn ${activeMode === 'chat' ? 'active' : ''}`}
          onClick={() => handleModeChange('chat')}
        >
          <svg viewBox="0 0 24 24">
            <path d="M4 4h16v12H7l-3 3V4z"/>
          </svg>
          Chat
        </button>
        <button
          className={`chat-voice-dock-btn ${activeMode === 'voice' ? 'active' : ''}`}
          onClick={() => handleModeChange('voice')}
        >
          <svg viewBox="0 0 24 24">
            <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"/>
          </svg>
          Voice
        </button>
      </nav>
    </div>
  );
};

export default VoiceChat;
